import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useMsal, useAccount } from '@azure/msal-react'
import { acquireApiToken } from '../utils/authTokenUtils'
import { getRuntimeConfig } from '../utils/configUtils'
import { TestItem, FilterType } from '../types/reports'
import TestSection from './TestSection'
import './CustomReport.css'
import './CustomReportPage.css'

interface DescribeBlock {
  title: string
  file: string
  tests: TestItem[]
}

const collectErrors = (result: any): string[] => {
  const errors: string[] = []
  if (result.error?.message) errors.push(result.error.message)
  if (result.errors && Array.isArray(result.errors)) {
    result.errors.forEach((err: any) => {
      if (err.message && !errors.includes(err.message)) errors.push(err.message)
    })
  }
  return errors
}

const stripTags = (title: string): string => title.replace(/@\w+/g, '').trim()

const formatDuration = (ms: number): string => {
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)
  if (minutes > 0) return `${minutes}m ${seconds}s`
  if (seconds > 0) return `${seconds}.${String(ms % 1000).padStart(3, '0')}s`
  return `${ms}ms`
}

const buildTestItems = (specs: any[]): TestItem[] => {
  const items: TestItem[] = []
  specs.forEach((spec: any) => {
    spec.tests?.forEach((test: any) => {
      if (!test.results?.length) return
      const totalDuration = test.results.reduce((sum: number, r: any) => sum + (r.duration || 0), 0)
      const lastResult = test.results[test.results.length - 1]
      const retryCount = test.results.length - 1
      const errors = collectErrors(lastResult)

      let status: string
      if (test.status === 'expected') status = 'passed'
      else if (test.status === 'unexpected') status = 'failed'
      else if (test.status === 'flaky') status = 'flaky'
      else status = 'skipped'

      items.push({
        key: `${spec.id || spec.title}-${test.projectName}`,
        title: spec.title,
        filename: retryCount > 0 ? `${test.projectName} — retried ${retryCount}x` : test.projectName,
        status,
        duration: totalDuration,
        errors,
      })
    })
  })
  return items
}

const isSetupFile = (suite: any): boolean => {
  const name = suite.file || suite.title || ''
  return name.includes('.setup.ts') || name.includes('data.validation.check')
}

const buildDescribeBlocks = (suites: any[]): DescribeBlock[] => {
  const blocks: DescribeBlock[] = []
  const setupTests: TestItem[] = []

  suites.forEach((fileSuite: any) => {
    if (isSetupFile(fileSuite)) {
      setupTests.push(...buildTestItems(fileSuite.specs || []))
    } else if (fileSuite.suites?.length > 0) {
      fileSuite.suites.forEach((describeSuite: any) => {
        const tests = buildTestItems(describeSuite.specs || [])
        if (tests.length > 0) {
          blocks.push({
            title: stripTags(describeSuite.title),
            file: fileSuite.file,
            tests,
          })
        }
      })
    } else if (fileSuite.specs?.length > 0) {
      const tests = buildTestItems(fileSuite.specs)
      if (tests.length > 0) {
        blocks.push({
          title: stripTags(fileSuite.title.split('/').pop()?.replace('.ts', '') || fileSuite.title),
          file: fileSuite.file,
          tests,
        })
      }
    }
  })

  if (setupTests.length > 0) {
    blocks.unshift({ title: 'Setup Tests', file: '', tests: setupTests })
  }

  return blocks
}

const parseUnicodeEscapes = (text: string): string => {
  if (!text) return text
  return (
    text
      .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '\r')
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, '\\')
      // eslint-disable-next-line no-control-regex
      .replace(/\[[0-9;]*m/g, '')
      .replace(/\\([^u])/g, '$1')
  )
}

const CustomReportPage: React.FC = () => {
  const { reportName } = useParams<{ reportName: string }>()
  const { instance } = useMsal()
  const account = useAccount()
  const [jsonData, setJsonData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState<Record<string, FilterType>>({})
  const [errorPopup, setErrorPopup] = useState<{ isOpen: boolean; messages: string[]; testTitle: string }>({
    isOpen: false,
    messages: [],
    testTitle: '',
  })

  useEffect(() => {
    if (!account || !reportName) return
    const fetchReport = async () => {
      try {
        const accessToken = await acquireApiToken(instance, account)
        const apiUrl = getRuntimeConfig('REACT_APP_API_URL')
        const res = await fetch(`${apiUrl}/api/reports/${reportName}/json`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        setJsonData(await res.json())
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch report')
      } finally {
        setLoading(false)
      }
    }
    fetchReport()
  }, [account, instance, reportName])

  const describeBlocks = useMemo(() => buildDescribeBlocks(jsonData?.suites ?? []), [jsonData])

  const toggleSection = (title: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      next.has(title) ? next.delete(title) : next.add(title)
      return next
    })
  }

  const handleErrorClick = (messages: string[], testTitle: string) => {
    setErrorPopup({ isOpen: true, messages, testTitle })
  }

  const closeErrorPopup = () => setErrorPopup({ isOpen: false, messages: [], testTitle: '' })

  const downloadJson = () => {
    if (!jsonData) return
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${reportName}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const stats = jsonData?.stats
  const startTime = stats?.startTime ? new Date(stats.startTime).toLocaleString() : null
  const duration = stats?.duration ? formatDuration(stats.duration) : null
  const fileId = reportName?.split('-').pop()

  const runInfo = stats?.runInfo
  const commitRepo: string | undefined = runInfo?.payload?.repo || runInfo?.repository
  const commitSha: string | undefined = runInfo?.payload?.sha
  const prUrl: string | undefined = runInfo?.payload?.prUrl
  const prNumber: string | undefined = runInfo?.payload?.prNumber
  const commitUrl = prUrl || (commitRepo && commitSha ? `https://github.com/${commitRepo}/commit/${commitSha}` : null)
  const commitLinkLabel = prUrl ? `PR #${prNumber}` : `Commit ${commitSha?.slice(0, 7)}`

  if (loading) return <div className="crp-loading">Loading report...</div>
  if (error) return <div className="crp-error">Error: {error}</div>

  return (
    <div className="crp-container">
      <div className="crp-header">
        <h2 className="crp-title">{reportName}</h2>
        <div className="crp-meta">
          {startTime && <span>Start: {startTime}</span>}
          {duration && <span>Duration: {duration}</span>}
          {fileId && <span>ID: {fileId}</span>}
          {commitUrl && (
            <a className="crp-commit-link" href={commitUrl} target="_blank" rel="noopener noreferrer">
              {commitLinkLabel}
            </a>
          )}
          {stats && (
            <span className="crp-stats">
              {stats.expected > 0 && <span className="crp-stat crp-passed">✅ {stats.expected} passed</span>}
              {stats.unexpected > 0 && <span className="crp-stat crp-failed">❌ {stats.unexpected} failed</span>}
              {stats.flaky > 0 && <span className="crp-stat crp-flaky">⚠️ {stats.flaky} flaky</span>}
              {stats.skipped > 0 && <span className="crp-stat crp-skipped">⏭ {stats.skipped} skipped</span>}
            </span>
          )}
          <button className="crp-download-btn" onClick={downloadJson}>
            Download JSON
          </button>
        </div>
      </div>

      <div className="crp-sections">
        {describeBlocks.length === 0 && <p className="crp-empty">No describe blocks found in this report.</p>}
        {describeBlocks.map(block => (
          <TestSection
            key={`${block.file}-${block.title}`}
            sectionTitle={block.title}
            cssClass="describe-block"
            ariaLabel={block.title}
            tests={block.tests}
            expanded={expandedSections.has(block.title)}
            onToggle={() => toggleSection(block.title)}
            filter={filters[block.title] || 'all'}
            onFilterChange={f => setFilters(prev => ({ ...prev, [block.title]: f }))}
            onErrorClick={handleErrorClick}
          />
        ))}
      </div>

      {errorPopup.isOpen && (
        <div className="error-popup-overlay" onClick={closeErrorPopup}>
          <div className="error-popup-modal" onClick={e => e.stopPropagation()}>
            <div className="error-popup-header">
              <h3>Error Details - {errorPopup.testTitle}</h3>
              <button className="close-popup-btn" onClick={closeErrorPopup} aria-label="Close error popup">
                ×
              </button>
            </div>
            <div className="error-popup-content">
              {errorPopup.messages.map((message, index) => (
                <div key={index} className="error-message-box">
                  <div className="error-message-header">Error {index + 1}</div>
                  <pre className="error-message-content">{parseUnicodeEscapes(message)}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomReportPage
